import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceGalaxy = () => {
  const [activeGalaxy, setActiveGalaxy] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const galaxies = [
    {
      id: 'engineering',
      name: 'Engineering Solutions',
      icon: 'Cog',
      color: 'from-brand-cyan to-brand-blue',
      description: 'Advanced engineering for tomorrow',
      capabilities: [
        'System Design & Architecture',
        'Technical Implementation',
        'Performance Optimization',
        'Quality Assurance & Testing',
        'Maintenance & Support',
        'Future-Ready Solutions'
      ],
      stats: { projects: '100+', satisfaction: '98%', roi: '340%' }
    },
    {
      id: 'telecommunications',
      name: 'Telecommunications',
      icon: 'Radio',
      color: 'from-brand-purple to-brand-indigo',
      description: 'Connected solutions for the future',
      capabilities: [
        'Network Design & Architecture',
        'Communication Systems',
        'Infrastructure Planning',
        'Signal Processing',
        'Wireless Solutions',
        'Network Optimization'
      ],
      stats: { projects: '80+', satisfaction: '99%', roi: '280%' }
    },
    {
      id: 'innovation',
      name: 'Technology Innovation',
      icon: 'Lightbulb',
      color: 'from-brand-orange to-brand-red',
      description: 'Pioneering tomorrow\'s solutions',
      capabilities: [
        'R&D and Prototyping',
        'Emerging Technology Integration',
        'Innovation Strategy',
        'Technology Roadmapping',
        'Patent Development',
        'Future-Tech Solutions'
      ],
      stats: { projects: '60+', satisfaction: '95%', roi: '220%' }
    },
    {
      id: 'sustainable',
      name: 'Sustainable Design',
      icon: 'Compass',
      color: 'from-brand-green to-brand-emerald',
      description: 'Building for people and society',
      capabilities: [
        'Sustainable Engineering',
        'Environmental Impact Assessment',
        'Green Technology Integration',
        'Social Impact Design',
        'Long-term Value Creation',
        'Continuous Improvement'
      ],
      stats: { projects: '50+', satisfaction: '100%', roi: '310%' }
    }
  ];

  return (
    <div className="relative py-20 bg-gradient-to-br from-secondary via-brand-navy to-primary overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-64 h-64 bg-brand-cyan/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-brand-purple/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-orange/20 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
            Explore Our Engineering
            <span className="holographic"> Solutions Universe</span>
          </h2>
          <p className="text-xl text-white max-w-3xl mx-auto font-body">
            Navigate through our four core areas of expertise, each containing specialized engineering capabilities designed to create sustainable, meaningful solutions with long-term impact.
          </p>
        </div>

        {/* Galaxy Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {galaxies.map((galaxy, index) => (
            <div
              key={galaxy.id}
              className={`relative group cursor-pointer transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
              onMouseEnter={() => setActiveGalaxy(galaxy.id)}
              onMouseLeave={() => setActiveGalaxy(null)}
            >
              <div className={`relative p-8 rounded-2xl bg-gradient-to-br ${galaxy.color} text-white overflow-hidden transform transition-all duration-500 hover:scale-105 hover:shadow-2xl`}>
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full transform translate-x-16 -translate-y-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full transform -translate-x-12 translate-y-12"></div>
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-white/20 rounded-lg mr-4">
                      <Icon name={galaxy.icon} size={32} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-bold">{galaxy.name}</h3>
                  </div>
                  
                  <p className="text-white/90 mb-6 text-lg">{galaxy.description}</p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{galaxy.stats.projects}</div>
                      <div className="text-sm text-white">Projects</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{galaxy.stats.satisfaction}</div>
                      <div className="text-sm text-white">Satisfaction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{galaxy.stats.roi}</div>
                      <div className="text-sm text-white">Avg ROI</div>
                    </div>
                  </div>

                  {/* Capabilities - Show on hover */}
                  <div className={`transition-all duration-500 ${
                    activeGalaxy === galaxy.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}>
                    <div className="border-t border-white/20 pt-4">
                      <h4 className="font-semibold mb-3">Core Capabilities:</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {galaxy.capabilities.map((capability, capIndex) => (
                          <div key={capIndex} className="flex items-center text-sm text-white/90">
                            <Icon name="CheckCircle" size={16} className="mr-2 text-white" />
                            {capability}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-6">
                    <Button
                      variant="outline"
                      className="bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-300"
                    >
                      Explore {galaxy.name}
                      <Icon name="ArrowRight" size={16} className="ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Interactive Service Selector CTA */}
        <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '800ms' }}>
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Not sure which galaxy fits your needs?
            </h3>
            <p className="text-gray-600 mb-6">
              Take our intelligent service selector to discover the perfect capabilities for your transformation journey.
            </p>
            <Button
              variant="default"
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              <Icon name="Compass" size={20} className="mr-2" />
              Find My Perfect Service Match
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceGalaxy;